/* eslint-disable no-param-reassign */
import React, { useMemo } from 'react';
import sanitizeHtml from 'sanitize-html';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Typography } from '@mui/material';
import { Pause, PlayArrowRounded } from '@mui/icons-material';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import route from '@src/constants/route';
import RunningWithErrorsRoundedIcon from '@mui/icons-material/RunningWithErrorsRounded';
import BadgeAvatar from '@src/components/BadgeAvatar';
import { capitalize } from 'lodash';
import { checkVietNam } from '@src/utils/checkCountry';
import {
  FEATURE,
  PACK_DURATION,
  PACKAGE_LEVEL,
  PACKAGE_LEVEL_V3,
  PACKAGE_TYPE,
} from '@src/constants/package';
import { SUB_STATUS } from '@src/constants/voiceCloning';
import { INIT_NOTIFICATION_DIALOG } from '@src/constants/dialog';
import { useCanUseEOLVoice } from '@src/hooks/useCanUseEOLVoice';
import { TTS_VERSION } from '@src/constants/tts';
import { VOICE_LEVEL, VOICE_PROVIDER } from '@src/constants/voice';
import { COLOR } from '@src/styles/color';
import { checkFeaturePermission } from '@src/services/tts';
import actions from '@src/redux/actions';
import useUserInfo from '@src/hooks/useTtsUser';
import CrownImg from '@src/assets/images/crown.png';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { convertFormatDate } from '@src/utils/date';
import { validateUserPackageCanUseVoiceCloning } from '@src/services/voiceCloning';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import { formatNameForEOL } from '@src/services/voice';
import { StyledVoiceItem } from './index.style';
import VoiceTag from './VoiceTag';

const VoiceItem = ({
  voice,
  activeVoiceId,
  usingPackageType,
  onChangePlayAudio,
  onSelectedVoice,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { getFeatureValue } = useFeatureFlags();
  const { usingPackage } = useSelector((state) => state.user);
  const { user } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();
  const { openSentences } = useSelector((state) => state.synthesisRequest);
  const { defaultGlobalVoice } = useSelector((state) => state.voice);
  const { usingDubbingPackage } = useSelector((state) => state.user);
  const isVietNam = checkVietNam();
  const { handleGetPackageCodeMapping } = useMappingPackage();
  const { canUseEOLVoice } = useCanUseEOLVoice();
  const history = useHistory();
  const isDefaultGlobalVoice =
    !isVietNam &&
    usingPackage?.level === PACKAGE_LEVEL.BASIC &&
    voice?.code === defaultGlobalVoice;

  const userInFeatureVoiceCloning = getFeatureValue(
    FEATURE_KEYS.VOICE_CLONING,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );
  const userVoiceEolEnabled = getFeatureValue(FEATURE_KEYS.USER_VOICE_EOL, {
    userId: user.id,
  });

  const canUseVoiceCloning = useMemo(
    () =>
      validateUserPackageCanUseVoiceCloning({
        ttsUser,
        voice,
        isUserInWhiteList: userInFeatureVoiceCloning,
      }),
    [ttsUser, voice, userInFeatureVoiceCloning],
  );

  const canUseVoice = () => {
    const isVoiceCloning =
      voice?.provider === VOICE_PROVIDER.VBEE_VOICE_CLONING &&
      voice?.userId === ttsUser?.id &&
      voice?.status === SUB_STATUS.PRIVATE;

    if (isVoiceCloning && !canUseVoiceCloning) return false;

    if (!voice?.features?.length) return true;
    // old packages version do not have field version
    const isNewPackageVersion =
      !usingPackage?.version || usingPackage?.version !== 'V1';

    let usingPackageByType =
      usingPackageType === PACKAGE_TYPE.DUBBING ? ttsUser?.dubbing : ttsUser;

    // new package can use both studio and dubbing
    if (isNewPackageVersion) {
      usingPackageByType = ttsUser;
    }

    if (!usingDubbingPackage && isDefaultGlobalVoice) return true;

    const eolVoice = voice?.eolDate;
    if (eolVoice && canUseEOLVoice({ voice, user: ttsUser })) return true;

    return voice.features.every((item) =>
      checkFeaturePermission(usingPackageByType?.features, item),
    );
  };

  const handleLimitedFeatureVoiceDialog = () => {
    const isBasicGlobalVoice = voice?.features?.includes(
      FEATURE.BASIC_GLOBAL_VOICE,
    );
    const isAdvancedGlobalVoice = voice?.features?.includes(
      FEATURE.ADVANCED_GLOBAL_VOICE,
    );

    if (isAdvancedGlobalVoice) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceUpgradeVip'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: 'sm',
          onUpgrade: () => {
            const upgradePackage = handleGetPackageCodeMapping({
              level: PACKAGE_LEVEL_V3.VIP,
              type: PACKAGE_TYPE.STUDIO,
              expiresIn: PACK_DURATION.YEAR,
            });
            history.push(`${route.PAYMENT}?package=${upgradePackage}`);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );

      return;
    }

    if (isBasicGlobalVoice) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceUpgradeSpecialOrVip'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: 'sm',
          onUpgrade: () => {
            history.push(route.PAYMENT);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );
      return;
    }

    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('limitedFeatureDesc', { feature: t('useThisVoice') }),
      }),
    );
  };

  const handleChangePlayAudio = (e) => {
    e.stopPropagation();
    onChangePlayAudio(voice);
  };

  const handleCloseNotificationDialog = () =>
    dispatch(
      actions.dialog.displayDialog({
        notificationDialog: INIT_NOTIFICATION_DIALOG,
      }),
    );

  const handleSelectedVoice = () => {
    if (voice?.version === TTS_VERSION.EMPHASIS && openSentences) {
      dispatch(
        actions.dialog.displayDialog({
          notificationDialog: {
            name: 'cannotUseVoice',
            title: t('cannotUseVoice'),
            description: t('cannotUseVoiceDes'),
            variant: 'warning',
            open: true,
            actionComponent: (
              <Button
                variant="contained"
                onClick={handleCloseNotificationDialog}
              >
                {t('understood')}
              </Button>
            ),
          },
        }),
      );
      return;
    }

    if (canUseVoice()) {
      onSelectedVoice(voice);
    } else {
      handleLimitedFeatureVoiceDialog();
    }
  };

  const styles =
    !!voice?.styles?.length && voice.styles.map((item) => t(item)).join(' - ');

  // const getImportantTagNameAdvancedVoice = () => {
  //   if (useVoiceCreditsByLevel && voice?.level === VOICE_LEVEL.ADVANCED) {
  //     return t(voice.level);
  //   }
  //   return '';
  // };

  // const getImportantTagName = () => {
  //   if (voice?.languageCode === VOICE_LEVEL.MULTILINGUAL)
  //     return t('multilingual');
  //   if (voice?.beta) return capitalize(t('beta'));
  //   if (voice?.level === VOICE_LEVEL.PREMIUM) return t(voice.level);

  //   return '';
  // };
  // const getImportantTagBg = () => {
  //   if (voice?.languageCode === VOICE_LEVEL.MULTILINGUAL)
  //     return COLOR.indigoColor;
  //   if (voice.id === activeVoiceId) return COLOR.white;
  //   if (openSentences && voice?.version === TTS_VERSION.EMPHASIS)
  //     return COLOR.lightText;
  //   if (voice?.beta) return COLOR.skyBlue;
  //   return COLOR.primary;
  // };

  const getLabels = () => {
    // Display level label except level BASIC
    const isAboutToBeRemoved = voice?.eolDate
      ? moment().isBefore(voice?.eolDate)
      : false;
    const daysLeftBeforeVoiceInactive = moment(voice?.eolDate).diff(
      moment(),
      'days',
    );
    const eolTagNameData = {
      textColor: COLOR.redPrimary,
      bgColor: COLOR.bgRedPrimary,
      title: isAboutToBeRemoved
        ? t(daysLeftBeforeVoiceInactive ? 'dayNumber' : 'today', {
            days: daysLeftBeforeVoiceInactive,
          })
        : undefined,
      icon: <RunningWithErrorsRoundedIcon className="returning-error-icon" />,
      contentTooltip: (
        <Typography
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(
              t(
                userVoiceEolEnabled
                  ? 'discontinuedAfterTimeV2'
                  : 'discontinuedAfterTime',
                {
                  date: moment(voice?.eolDate)?.format('DD/MM/YYYY'),
                  name: formatNameForEOL(voice?.name, userVoiceEolEnabled),
                },
              ),
              {
                allowedTags: ['b', 'span'],
                allowedAttributes: {
                  '*': ['style'],
                },
              },
            ),
          }}
        />
      ),
    };
    const levelTagNameData = {
      textColor: COLOR.darkBlue,
      bgColor: COLOR.primary,
      title: voice?.level !== VOICE_LEVEL.BASIC ? t(voice.level) : undefined,
    };
    const betaTagNameData = {
      textColor: COLOR.blueV2,
      bgColor: COLOR.skyBlue,
      title: voice?.beta ? capitalize(t('beta')) : undefined,
    };

    const isPublicVoiceCloning = voice?.status === SUB_STATUS.PUBLIC;
    const isVoiceCloningAboutToRemoved =
      isPublicVoiceCloning && voice?.discardAt
        ? moment().isBefore(voice?.discardAt)
        : false;

    const daysLeftBeforeVoiceCloningDiscard =
      isPublicVoiceCloning && moment(voice?.discardAt).diff(moment(), 'days');

    const voiceCloningRetentionDays = isPublicVoiceCloning
      ? voice?.retentionDays
      : null;

    const publicVoiceCloningTagData = {};
    if (isPublicVoiceCloning) {
      publicVoiceCloningTagData.icon = isVoiceCloningAboutToRemoved ? (
        <RunningWithErrorsRoundedIcon className="returning-error-icon" />
      ) : null;

      publicVoiceCloningTagData.title = isVoiceCloningAboutToRemoved
        ? t(daysLeftBeforeVoiceCloningDiscard ? 'dayNumber' : 'today', {
            days: daysLeftBeforeVoiceCloningDiscard,
          })
        : t('dayNumber', {
            days: voiceCloningRetentionDays,
          });

      publicVoiceCloningTagData.textColor = isVoiceCloningAboutToRemoved
        ? COLOR.redPrimary
        : COLOR.darkBlue;

      publicVoiceCloningTagData.bgColor = isVoiceCloningAboutToRemoved
        ? COLOR.bgRedPrimary
        : COLOR.skyBlue;

      publicVoiceCloningTagData.contentTooltip = (
        <Typography sx={{ fontSize: '14px' }}>
          {isVoiceCloningAboutToRemoved
            ? t('voiceCloningDiscardAt', {
                date: convertFormatDate(voice?.discardAt),
              })
            : t('voiceCloningRetentionDays', {
                days: voiceCloningRetentionDays,
              })}
        </Typography>
      );
    }

    const isChoosingVoice = voice.id === activeVoiceId;
    if (isChoosingVoice) {
      levelTagNameData.bgColor = COLOR.white;
      levelTagNameData.textColor = COLOR.black.default;

      betaTagNameData.bgColor = COLOR.white;
      betaTagNameData.textColor = COLOR.black.default;
    }

    return [
      eolTagNameData,
      betaTagNameData,
      levelTagNameData,
      publicVoiceCloningTagData,
    ].filter((item) => item?.title);
  };

  return (
    <StyledVoiceItem
      disabledVoice={openSentences && voice?.version === TTS_VERSION.EMPHASIS}
      active={voice.id === activeVoiceId}
      playingAudio={voice.playStatus}
      onClick={handleSelectedVoice}
      variant="outlined"
      data-id="select-voice"
      data-voice-code={voice?.code}
      aria-label={`${voice.name} voice`}
    >
      <VoiceTag getLabels={getLabels()} />
      <div className="voice-info">
        <BadgeAvatar
          img={voice.roundImage}
          smallImg={voice && voice.language && voice.language.roundImage}
          type="image"
        />
        <div className="detail-voice">
          <Typography variant="h6" className="voice-name">
            {voice.name}
            {voice?.creditFactor > 1 && (
              <span className="x2-char-tag">x{voice?.creditFactor}</span>
            )}
          </Typography>
          <Typography variant="subtitle1" className="features">
            {styles}
          </Typography>
        </div>
      </div>
      <IconButton
        onClick={handleChangePlayAudio}
        aria-label="play"
        data-id="preview-voice"
        data-voice-code={voice?.code}
      >
        {voice.playStatus ? (
          <Pause className="play-icon" fontSize="large" />
        ) : (
          <PlayArrowRounded className="play-icon" fontSize="large" />
        )}
      </IconButton>
    </StyledVoiceItem>
  );
};
export default VoiceItem;
